import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaEnvelopeOpenText, FaUserFriends } from 'react-icons/fa';
import { MdMarkEmailRead, MdOutlineMailLock } from 'react-icons/md';
import toastService from '../services/toastService';
import {
  useSendOtpMutation,
  useVerifyOtpMutation,
  useRegisterMutation,
  useGetAllBrandsQuery,
} from '../app/services/authApi';
import {
  userLoginSuccess,
  userPartialRegister,
  userUpdateViewType,
} from '../app/reducers/userInfo/slice';
import { addBrand } from '../app/reducers/myBrand/slice';
import useFileUpload from '../hooks/fileUpload';
import logoSrc2 from '../assets/logo.png';
import termsPdf from '../assets/BrandSellerTermsOfService.pdf';
import Loader from '../Components/Ui/Loader';

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Component State
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [currentSessionId, setSessionId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);
  // State to hold verified contact info for a new user
  const [verifiedContact, setVerifiedContact] = useState({ email: null, phone: null });

  // Role and Form State
  const [isSeller, setIsSeller] = useState(false);
  const [brandGst, setBrandGst] = useState('');
  const [sellerGst, setSellerGst] = useState('');
  const [brandName, setBrandName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [name, setName] = useState('');
  const [brandLogo, setBrandLogo] = useState(null); // eslint-disable-line no-unused-vars
  const [poc, setPoc] = useState('');
  const [pocMobile, setPocMobile] = useState('');
  const [alsoSeller, setAlsoSeller] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [sellerAuthCertificates, setSellerAuthCertificates] = useState({});
  const [brandGstCertificate, setBrandGstCertificate] = useState([]);
  const [sellerGstCertificate, setSellerGstCertificate] = useState(null);
  const [errors, setErrors] = useState({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const inputRefs = React.useMemo(
    () => ({
      phone: React.createRef(),
      otp: Array(6)
        .fill(0)
        .map(() => React.createRef()),
      email: React.createRef(),
      brandGst: React.createRef(),
      sellerGst: React.createRef(),
      tradeName: React.createRef(),
      Name: React.createRef(),
      brandName: React.createRef(),
      poc: React.createRef(),
      pocMobile: React.createRef(),
      addressLine1: React.createRef(),
      addressLine2: React.createRef(),
      city: React.createRef(),
      state: React.createRef(),
      zipCode: React.createRef(),
    }),
    [],
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Tab') {
        const focusableElements = Object.values(inputRefs)
          .flatMap((ref) => (Array.isArray(ref) ? ref.map((r) => r.current) : ref.current))
          .filter((el) => el);

        const currentIndex = focusableElements.findIndex((el) => el === document.activeElement);

        if (currentIndex !== -1 && currentIndex < focusableElements.length - 1) {
          e.preventDefault();
          focusableElements[currentIndex + 1].focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [inputRefs]);

  // RTK Query Hooks & Redux State
  const [sendOtp, { isLoading: isSendingOtp }] = useSendOtpMutation();
  const [register, { isLoading: isRegistering }] = useRegisterMutation();
  const [verifyOtp, { isLoading: isVerifyingOtp }] = useVerifyOtpMutation();
  const { uploadFile, uploading } = useFileUpload();
  // The _id will only exist for users who are already in the DB
  const {
    user: { _id },
  } = useSelector((state) => state.userInfo);
  const { data: brandsData } = useGetAllBrandsQuery(undefined, {
    skip: !isSeller,
  });

  const brandOptions =
    brandsData?.data.map((b) => ({
      id: b._id,
      name: b.name,
    })) || [];

  useEffect(() => {
    if (step === 1) inputRefs.phone.current?.focus();
    else if (step === 2) inputRefs.otp[0].current?.focus();
    else if (step === 3) inputRefs.email.current?.focus();
    else if (step === 4) inputRefs.otp[0].current?.focus();
    else if (step === 6) inputRefs.brandGst.current?.focus();
    else if (step === 7) inputRefs.sellerGst.current?.focus();
  }, [step, inputRefs]);

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  // --- Event Handlers ---

  const handleNext = () => {
    if (step === 1 && (!phone || !/^\d{10}$/.test(phone))) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    if ((step === 2 || step === 4) && (!otp || otp.length < 6)) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    if (step === 3 && (!email || !/^\S+@\S+\.\S+$/.test(email))) {
      setError('Please enter a valid email address');
      return;
    }
    setError('');
    if (step === 2) {
      setResendTimer(0);
      setCanResend(true);
    }
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (step > 0) {
      if (step === 1) {
        navigate('/login');
      } else {
        setStep((prev) => prev - 1);
      }
      setOtp('');
      setError('');
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    try {
      // The `isNewUser` flag is no longer needed as the backend handles this
      const response = await sendOtp({ phoneNumber: phone }).unwrap();
      if (response?.success) {
        setSessionId(response?.sessionId);
        setCanResend(false);
        setResendTimer(45);
        handleNext();
        toastService.success('OTP sent successfully! Please check your messages.');
      } else {
        toastService.error(response?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      toastService.error(err?.data?.message || 'Failed to send OTP.');
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    try {
      const response = await verifyOtp({ phoneOtp: otp, sessionId: currentSessionId }).unwrap();
      if (response?.success) {
        // If the API returns a user object, they exist in the DB.
        if (response.user) {
          dispatch(userPartialRegister(response.user));
        }
        // If it's a new user, the API sends back verified contact info.
        if (response.isNewUser) {
          setVerifiedContact(response.verifiedContact);
        }
        handleNext();
        setOtp('');
        toastService.success('OTP verified successfully!');
      } else {
        toastService.error(response?.message || 'OTP Verification failed, try again');
      }
    } catch (err) {
      toastService.error(err?.data?.message || 'OTP Verification failed.');
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setError('');
    try {
      const response = await sendOtp({ email: email, sessionId: currentSessionId }).unwrap();
      if (response?.success) {
        setSessionId(response?.sessionId);
        setCanResend(false);
        setResendTimer(45);
        handleNext();
        toastService.success('OTP sent to your email successfully! Please check your inbox.');
      } else {
        toastService.error(response?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      toastService.error(err?.data?.message || 'An error occurred while sending OTP.');
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    try {
      const response = await verifyOtp({ emailOtp: otp, sessionId: currentSessionId }).unwrap();
      if (response?.success) {
        // If the API returns a user object, they exist in the DB.
        if (response.user) {
          dispatch(userPartialRegister(response.user));
        }
        // If it's a new user, the API sends back verified contact info.
        if (response.isNewUser) {
          setVerifiedContact(response.verifiedContact);
        }
        handleNext();
        setOtp('');
        toastService.success('Email OTP verified successfully!');
      } else {
        toastService.error(response?.message || 'OTP Verification failed, try again');
      }
    } catch (err) {
      toastService.error(err?.data?.message || 'OTP Verification failed.');
    }
  };

  const handlePartnerSelection = (type) => {
    if (type === 'Brand') {
      setStep(6);
    } else if (type === 'Seller') {
      setIsSeller(true);
      setStep(7);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setCanResend(false);
    setResendTimer(45);
    try {
      let payload = {};
      if (step === 2) {
        payload = { phoneNumber: phone };
      } else if (step === 4) {
        payload = { email: email, sessionId: currentSessionId };
      }
      await sendOtp(payload).unwrap();
      toastService.success('OTP resent successfully!');
    } catch (err) {
      toastService.error(err?.data?.message || 'Failed to resend OTP.');
    }
  };

  // --- OTP Input Handlers ---
  const handleOtpChange = (e, i) => {
    const value = e.target.value;
    if (/^\d?$/.test(value)) {
      const newOtp = otp.split('');
      newOtp[i] = value;
      setOtp(newOtp.join(''));
      if (value && i < 5) document.getElementById(`otp-${i + 1}`)?.focus();
    }
  };

  const handleOtpBackspace = (e, i) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      document.getElementById(`otp-${i - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasted)) {
      setOtp(pasted);
      pasted.split('').forEach((char, index) => {
        const input = document.getElementById(`otp-${index}`);
        if (input) input.value = char;
      });
      document.getElementById('otp-5')?.focus();
    }
  };

  // --- File and Form Change Handlers ---
  const handleFileChange = async (event, type, brandId) => {
    const files = event.target.files;
    if (files.length > 0) {
      let file = files[0];

      // Check if the file object has a name or type, if not, it might be a file from Google Drive
      if (!file.name || !file.type) {
        console.warn('File object missing name or type, attempting to resolve:', file);
        try {
          // If the file object has a URI, fetch it
          if (file.uri) {
            const response = await fetch(file.uri);
            const blob = await response.blob();
            file = new File([blob], file.name || 'untitled_file', {
              type: blob.type || 'application/octet-stream',
            });
          } else {
            const arrayBuffer = await file.arrayBuffer();
            const blob = new Blob([arrayBuffer], { type: file.type || 'application/octet-stream' });
            // Create a new File object with a default name and type if original is missing
            file = new File([blob], file.name || 'untitled_file', {
              type: file.type || 'application/octet-stream',
            });
          }
        } catch (error) {
          console.error('Error resolving file from Google Drive:', error);
          toastService.error(
            'Failed to read file from source. Please try again or select a local file.',
          );
          return;
        }
      }

      if (type === 'brandGstCertificates') {
        setBrandGstCertificate(Array.from(files));
      } else if (type === 'sellerGstCertificate') {
        setSellerGstCertificate(file);
      } else if (type === 'brandLogo') {
        setBrandLogo(file);
      } else if (type === 'sellerAuthCertificates' && brandId) {
        setSellerAuthCertificates((prev) => ({
          ...prev,
          [brandId]: file,
        }));
      }
    }
  };

  const handleBrandChange = (e) => {
    const brandId = e.target.value;
    const isChecked = e.target.checked;
    if (isChecked) {
      setSelectedBrands([...selectedBrands, brandId]);
    } else {
      setSelectedBrands(selectedBrands.filter((id) => id !== brandId));
      const newAuthCerts = { ...sellerAuthCertificates };
      delete newAuthCerts[brandId];
      setSellerAuthCertificates(newAuthCerts);
    }
  };

  // --- Form Validation & Submission ---
  const validateForm = () => {
    const newErrors = {};
    // Common validations
    if (!tradeName.trim()) newErrors.tradeName = 'Trade Name is required';

    // Brand specific validations
    if (step === 6) {
      if (!brandName.trim()) newErrors.brandName = 'Brand Name is required';
      if (!poc.trim()) newErrors.poc = 'POC Name is required';
      if (!pocMobile.trim()) newErrors.pocMobile = 'POC Mobile Number is required';
      else if (!/^[6-9]\d{9}$/.test(pocMobile))
        newErrors.pocMobile = 'Invalid mobile number format';
      if (!brandGst.trim()) newErrors.brandGst = 'Brand GSTIN is required';
      else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(brandGst))
        newErrors.brandGst = 'Invalid Brand GSTIN format';
      if (brandGstCertificate.length === 0)
        newErrors.brandGstCertificate = 'Brand GST Registration Certificate is required';
    }
    if (!agreedToTerms) newErrors.agreedToTerms = 'You must agree to the Terms and Conditions';

    // Seller specific validations (if not brand, or if alsoSeller)
    if (step === 7 || (step === 6 && alsoSeller)) {
      if (!name.trim()) newErrors.name = 'Owner Name is required';
      if (!sellerGst.trim()) newErrors.sellerGst = 'Seller GSTIN is required';
      else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(sellerGst))
        newErrors.sellerGst = 'Invalid Seller GSTIN format';
      if (!sellerGstCertificate)
        newErrors.sellerGstCertificate = 'Seller GST Registration Certificate is required';
      if (!addressLine1.trim()) newErrors.addressLine1 = 'Address line 1 is required';
      if (!city.trim()) newErrors.city = 'City is required';
      if (!state.trim()) newErrors.state = 'State is required';
      if (!zipCode.trim()) newErrors.zipCode = 'ZIP / Postcode is required';
      else if (!/^\d{6}$/.test(zipCode)) newErrors.zipCode = 'Invalid ZIP code format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!validateForm()) {
      toastService.error('Please fix the errors before submitting.');
      setIsSubmitting(false);
      return;
    }

    try {
      const roles = [];
      if (step === 6) roles.push('brand');
      if (step === 7 || (step === 6 && alsoSeller)) roles.push('seller');

      // Base payload with common data
      let payload = {
        sessionId: currentSessionId,
        role: roles,
        tradeName,
        name,
      };

      // Conditionally add user identifiers
      if (_id) {
        // If _id exists, it's an existing user completing registration.
        payload.userId = _id;
      } else {
        // Otherwise, it's a new user. Pass the verified contact info.
        payload.email = verifiedContact.email;
        payload.phone = verifiedContact.phone;
      }

      // Append Brand-specific data
      if (step === 6) {
        payload.brandName = brandName;
        payload.pocName = poc;
        payload.pocMobile = pocMobile;
        payload.brandGst = brandGst;
        if (brandLogo) {
          const brandLogoUrl = await uploadFile(brandLogo, `brand/${_id || 'new'}/logo`);
          payload.brandLogo = brandLogoUrl;
        }
        if (brandGstCertificate.length > 0) {
          const uploadedUrls = await Promise.all(
            brandGstCertificate.map((file) =>
              uploadFile(file, `brand/${_id || 'new'}/gstCertificates`),
            ),
          );
          payload.brandGstCertificate = uploadedUrls; // Changed to brandGstCertificate
        }
      }

      // Append Seller-specific data
      if (step === 7 || (step === 6 && alsoSeller)) {
        payload.address = { line1: addressLine1, line2: addressLine2, city, state, zipCode };
        payload.sellerGst = sellerGst;
        if (sellerGstCertificate) {
          const gstUrl = await uploadFile(
            sellerGstCertificate,
            `seller/${_id || 'new'}/gstCertificate`,
          );
          payload.sellerGstCertificate = gstUrl; // Changed to sellerGstCertificate
        }

        // Ensure brandDeal is always an array if seller role is present
        payload.brandDeal = [];
        if (selectedBrands.length > 0) {
          payload.brandDeal = await Promise.all(
            selectedBrands.map(async (brandId) => {
              let authCertUrl = '';
              if (sellerAuthCertificates[brandId]) {
                authCertUrl = await uploadFile(
                  sellerAuthCertificates[brandId],
                  `seller/${_id || 'new'}/authCertificates`,
                );
              }
              return { brand: brandId, sellerAuthenticate: authCertUrl };
            }),
          );
        }
      }

      const response = await register(payload).unwrap();

      toastService.success('Registration successful!');

      // Check if response contains token for auto-login
      if (response.token) {
        const userData = {
          ...response.user,
          token: response.token,
          userId: response.userId,
          role: response.role,
        };
        dispatch(userLoginSuccess(userData));
      } else {
        dispatch(userLoginSuccess(response.user || {}));
      }

      if (response.myBrand) {
        dispatch(addBrand(response.myBrand || {}));
      }

      const userRoles = Array.isArray(response.user?.role) ? response.user.role : [];
      if (userRoles.includes('brand') && userRoles.includes('seller')) {
        navigate('/select-view');
      } else if (userRoles.includes('brand')) {
        dispatch(userUpdateViewType('brand')); // Add this line
        navigate('/brand/products'); // Consistent with Login.jsx
      } else if (userRoles.includes('seller')) {
        dispatch(userUpdateViewType('seller'));
        navigate('/seller/brand-catalog'); // Consistent with Login.jsx
      } else {
        // If no specific brand or seller role, navigate to a sensible default or handle
        // For now, keeping the previous behavior of navigating to seller dashboard if not brand
        navigate('/seller/brand-catalog');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      toastService.error(
        err?.data?.message || err?.message || 'An error occurred during registration',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Render Functions ---
  const renderMobileCard = (children) => (
    <div className="min-h-screen flex items-center justify-center secondary-background px-4">
      <div className="w-full max-w-2xl tertiary-background rounded-3xl py-6 mt-4 px-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          {step > 0 ? (
            <button onClick={handleBack} className="text-primary text-3xl font-bold leading-none">
              ←
            </button>
          ) : (
            <div className="w-6" />
          )}

          <div className="flex items-center justify-center h-[60px] ">
            <div className=" flex items-center">
              <img src={logoSrc2} alt="logoAlt1" className="w-[145px] h-[130px]" />
            </div>
          </div>
          <div className="w-6" />
        </div>
        {error && <div className="text-red-500 text-center text-sm mb-4">{error}</div>}
        {children}
      </div>
    </div>
  );

  return (
    <>
      {step === 1 &&
        renderMobileCard(
          <>
            <div className="text-center mb-6">
              {/* <h2 className="text-lg font-semibold text-primary mb-2">Sign Up</h2> */}
              <p className="text-sm text-secondary mb-4">
                We will send you a one-time password
                <br />
                to this mobile number
              </p>
            </div>

            <div className="flex justify-center">
              <div className="tertiary-background rounded-xl shadow-md border px-6 py-2 w-64">
                <span className="text-secondary">+91 | </span>
                <input
                  type="tel"
                  placeholder="9876543210"
                  className="border-none outline-none text-center text-base w-40 tertiary-background"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  maxLength="10"
                  pattern="[0-9]{10}"
                  title="Phone number must be 10 digits"
                />
              </div>
            </div>

            {/* Button */}
            <div className="flex justify-center mt-6">
              <button
                onClick={handleSendOtp}
                className="w-1/2 md:w-1/4 primary-background white-text py-2 rounded-xl font-semibold text-sm"
              >
                Get OTP
              </button>
            </div>
          </>,
        )}

      {step === 2 &&
        renderMobileCard(
          <>
            <div className="text-center mb-6">
              <div className="h-20 w-20 mx-auto mb-6 secondary-background rounded-full flex items-center justify-center">
                <FaEnvelopeOpenText className="text-4xl text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-primary mb-2">OTP Verification</h2>
              <p className="text-sm text-secondary mb-7">Enter the OTP sent to +91 {phone}</p>
            </div>
            <div className="flex justify-center gap-2 sm:gap-3 mb-6" onPaste={handleOtpPaste}>
              {[...Array(6)].map((_, i) => (
                <input
                  key={i}
                  id={`otp-${i}`}
                  type="text"
                  maxLength="1"
                  className="w-10 h-10 sm:w-12 sm:h-12 text-center tertiary-background border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 border-color"
                  value={otp[i] || ''}
                  onChange={(e) => handleOtpChange(e, i)}
                  onKeyDown={(e) => handleOtpBackspace(e, i)}
                  ref={inputRefs.otp[i]}
                />
              ))}
            </div>
            <div className="text-center mb-6">
              <p className="text-sm text-secondary">
                Didn't receive the OTP?{' '}
                <button
                  onClick={handleResendOtp}
                  className="font-semibold text-primary hover:underline"
                  disabled={!canResend || resendTimer > 0}
                >
                  {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                </button>
              </p>
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleVerifyOtp}
                className="w-1/2 md:w-1/4 primary-background white-text py-2 rounded-xl font-semibold text-sm"
              >
                Continue
              </button>
            </div>
          </>,
        )}

      {step === 3 &&
        renderMobileCard(
          <>
            <div className="text-center mb-6">
              <div className="h-20 w-20 mx-auto mb-6 secondary-background rounded-full flex items-center justify-center">
                <MdOutlineMailLock className="text-4xl text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-primary mb-2">Enter Email ID</h2>
              <p className="text-sm text-secondary mb-10">
                We will send you a One Time Passcode
                <br />
                to this email address
              </p>
            </div>
            <div className="flex justify-center">
              <div className="tertiary-background rounded-xl border shadow-md px-4 py-2 mb-10 w-full max-w-sm">
                <input
                  type="email"
                  placeholder="abc@email.com"
                  className="w-full border-none outline-none text-center text-base tertiary-background"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleEmailSubmit}
                className="w-1/2 lg:w-1/4 primary-background white-text py-2 rounded-xl font-semibold text-sm"
              >
                Get OTP
              </button>
            </div>
          </>,
        )}

      {step === 4 &&
        renderMobileCard(
          <>
            <div className="text-center mb-6">
              <div className="h-20 w-20 mx-auto mb-6 secondary-background rounded-full flex items-center justify-center">
                <MdMarkEmailRead className="text-4xl text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-primary mb-2">Email Verification</h2>
              <p className="text-sm text-secondary mb-4">Enter OTP sent to your email</p>
            </div>
            <div className="flex justify-center gap-2 sm:gap-3 mb-6" onPaste={handleOtpPaste}>
              {[...Array(6)].map((_, i) => (
                <input
                  key={i}
                  id={`otp-${i}`}
                  type="text"
                  maxLength="1"
                  className="w-10 h-10 sm:w-12 sm:h-12 text-center tertiary-background border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 border-color"
                  value={otp[i] || ''}
                  onChange={(e) => handleOtpChange(e, i)}
                  onKeyDown={(e) => handleOtpBackspace(e, i)}
                  ref={inputRefs.otp[i]}
                />
              ))}
            </div>
            <div className="text-center mb-6">
              <p className="text-sm text-secondary">
                Didn't receive the OTP?{' '}
                <button
                  onClick={handleResendOtp}
                  className="font-semibold text-primary hover:underline"
                  disabled={!canResend || resendTimer > 0}
                >
                  {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                </button>
              </p>
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleVerifyEmail}
                className="w-1/2 md:w-1/4 primary-background white-text py-2 rounded-xl font-semibold text-sm"
              >
                Continue
              </button>
            </div>
          </>,
        )}

      {step === 5 &&
        renderMobileCard(
          <>
            <div className="text-center mb-6">
              <div className="h-20 w-20 mx-auto mb-6 secondary-background rounded-full flex items-center justify-center">
                <FaUserFriends className="text-4xl text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-primary mb-2">Select Partnership Type</h2>
              <p className="text-sm text-secondary mb-10">
                How do you want to partner with Winndo?
              </p>
            </div>
            <div className="flex justify-center gap-6 mb-12">
              <button
                className="w-1/3 primary-background white-text py-2 rounded-xl font-medium text-sm"
                onClick={() => handlePartnerSelection('Brand')}
              >
                Brand
              </button>
              <button
                className="w-1/3 primary-background white-text py-2 rounded-xl font-medium text-sm"
                onClick={() => handlePartnerSelection('Seller')}
              >
                Seller
              </button>
            </div>
          </>,
        )}

      {step === 6 &&
        renderMobileCard(
          <>
            <div className="text-center mb-8">
              <h2 className="text-xl lg:text-2xl font-semibold text-primary">Brand Information</h2>
              <p className="text-sm text-secondary">Fill in the following details to continue</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 text-left">
              {/* Common Fields */}
              <div>
                <label className="text-sm font-medium text-secondary">GSTIN</label>
                <input
                  type="text"
                  placeholder="Enter GSTIN"
                  className="mt-1 w-full  px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                  ref={inputRefs.brandGst}
                  value={brandGst}
                  onChange={(e) => setBrandGst(e.target.value.toUpperCase())}
                />
                {errors.brandGst && <span className="text-red-500 text-xs">{errors.brandGst}</span>}
              </div>
              <div>
                <label className="text-sm font-medium text-secondary">Trade Name</label>
                <input
                  type="text"
                  placeholder="Enter Trade Name"
                  className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                  ref={inputRefs.tradeName}
                  value={tradeName}
                  onChange={(e) => setTradeName(e.target.value)}
                />
                {errors.tradeName && (
                  <span className="text-red-500 text-xs">{errors.tradeName}</span>
                )}
              </div>

              {/* Brand-Specific Fields */}
              <div>
                <label className="text-sm font-medium text-secondary">Brand Name</label>
                <input
                  type="text"
                  placeholder="Enter Brand Name"
                  className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                />
                {errors.brandName && (
                  <span className="text-red-500 text-xs">{errors.brandName}</span>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-secondary">
                  GST Registration Certificate(s)
                </label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange(e, 'brandGstCertificates')}
                  className="mt-1 w-full px-4 py-2 border rounded-lg text-sm bg-white file:mr-4 file:py-1 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:primary-background file:white-text hover:file:bg-[#E95B21]"
                />
                {errors.brandGstCertificate && (
                  <span className="text-red-500 text-xs">{errors.brandGstCertificate}</span>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-secondary">Brand Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'brandLogo')}
                  className="mt-1 w-full px-4 py-2 border rounded-lg text-sm bg-white file:mr-4 file:py-1 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:primary-background file:white-text hover:file:bg-[#E95B21]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-secondary">Point Of Contact (POC)</label>
                <input
                  type="text"
                  placeholder="Enter Name"
                  className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                  value={poc}
                  onChange={(e) => setPoc(e.target.value)}
                />
                {errors.poc && <span className="text-red-500 text-xs">{errors.poc}</span>}
              </div>
              <div>
                <label className="text-sm font-medium text-secondary">POC Mobile Number</label>
                <input
                  type="tel"
                  placeholder="Enter Mobile Number"
                  className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                  value={pocMobile}
                  onChange={(e) => setPocMobile(e.target.value.replace(/\D/g, ''))}
                  maxLength="10"
                  pattern="[6-9]{1}[0-9]{9}"
                  title="Mobile number must be 10 digits and start with 6, 7, 8, or 9"
                />
                {errors.pocMobile && (
                  <span className="text-red-500 text-xs">{errors.pocMobile}</span>
                )}
              </div>

              {/* Also Seller Toggle */}
              <div className="flex items-center justify-between border px-4 py-2 rounded-lg bg-gray-50">
                <span className="text-sm font-medium">Do you also want to become a seller?</span>
                <label className="inline-flex relative items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={alsoSeller}
                    onChange={(e) => setAlsoSeller(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-[#E95B21] transition-colors duration-300"></div>
                  <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-300 transform peer-checked:translate-x-5"></div>
                </label>
              </div>

              {/* Seller Information (Conditional) */}
              {alsoSeller && (
                <div className="space-y-4 border rounded-xl p-4 bg-gray-50">
                  <h3 className="text-base font-semibold text-primary mb-2 text-center">
                    Seller GST Information
                  </h3>
                  <div>
                    <label className="text-sm font-medium text-secondary">GSTIN</label>
                    <input
                      type="text"
                      placeholder="Enter GSTIN"
                      className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                      ref={inputRefs.sellerGst}
                      value={sellerGst}
                      onChange={(e) => setSellerGst(e.target.value.toUpperCase())}
                    />
                    {errors.sellerGst && (
                      <span className="text-red-500 text-xs">{errors.sellerGst}</span>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-secondary">
                      GST Registration Certificate
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'sellerGstCertificate')}
                      className="mt-1 w-full px-4 py-2 border rounded-lg text-sm bg-white file:mr-4 file:py-1 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:primary-background file:white-text focus:outline-none focus:ring-2 border-color  hover:file:bg-[#E95B21]"
                    />
                    {errors.sellerGstCertificate && (
                      <span className="text-red-500 text-xs">{errors.sellerGstCertificate}</span>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-secondary">Owner Name</label>
                    <input
                      type="text"
                      placeholder="Enter Owner Name"
                      className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                    {errors.name && <span className="text-red-500 text-xs">{errors.name}</span>}
                  </div>
                  <h3 className="text-base font-semibold text-primary mb-2 text-center">
                    Seller Address Information
                  </h3>
                  <div>
                    <label className="text-sm font-medium text-secondary">Address line 1</label>
                    <input
                      type="text"
                      placeholder="Enter address line 1"
                      className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                    />
                    {errors.addressLine1 && (
                      <span className="text-red-500 text-xs">{errors.addressLine1}</span>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-secondary">Address line 2</label>
                    <input
                      type="text"
                      placeholder="Enter address line 2"
                      className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-4">
                    <div className="w-1/2">
                      <label className="text-sm font-medium text-secondary">City</label>
                      <input
                        type="text"
                        placeholder="Enter city"
                        className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                      {errors.city && <span className="text-red-500 text-xs">{errors.city}</span>}
                    </div>
                    <div className="w-1/2">
                      <label className="text-sm font-medium text-secondary">State</label>
                      <input
                        type="text"
                        placeholder="Enter state"
                        className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                      />
                      {errors.state && <span className="text-red-500 text-xs">{errors.state}</span>}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-secondary">ZIP / Postcode</label>
                    <input
                      type="text"
                      placeholder="Enter ZIP / postcode"
                      className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                    />
                    {errors.zipCode && (
                      <span className="text-red-500 text-xs">{errors.zipCode}</span>
                    )}
                  </div>
                </div>
              )}
              {/* <Terms and Conditions Checkbox> */}
              <div className="flex items-center mt-4 ml-1">
                <input
                  type="checkbox"
                  id="termsBrand"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="text-[#E95B21] mr-2"
                />
                <label htmlFor="termsBrand" className="text-sm text-secondary">
                  I agree to the{' '}
                  <a
                    className="hover:underline hover:text-orange-600"
                    href={termsPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms and Conditions
                  </a>
                </label>
              </div>
              {errors.agreedToTerms && (
                <span className="text-red-500 text-xs">{errors.agreedToTerms}</span>
              )}

              <div className="flex justify-center pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting || uploading || !agreedToTerms}
                  className="w-1/2 md:w-1/3 primary-background white-text py-2 rounded-xl font-medium text-sm transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting || uploading ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </>,
        )}

      {step === 7 &&
        renderMobileCard(
          <>
            <div className="text-center mb-8">
              <h2 className="text-xl lg:text-2xl font-semibold text-primary mb-2">
                Seller Information
              </h2>
              <p className="text-sm text-secondary">Please fill the required details</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 text-left">
              <div>
                <label className="text-sm font-medium text-secondary">GSTIN</label>
                <input
                  type="text"
                  placeholder="Enter GSTIN"
                  className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                  ref={inputRefs.sellerGst}
                  value={sellerGst}
                  onChange={(e) => setSellerGst(e.target.value.toUpperCase())}
                />
                {errors.sellerGst && (
                  <span className="text-red-500 text-xs">{errors.sellerGst}</span>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-secondary">
                  GST Registration Certificate
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange(e, 'sellerGstCertificate')}
                  className="mt-1 w-full px-4 py-2 border rounded-lg text-sm bg-white file:mr-4 file:py-1 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:primary-background file:white-text focus:outline-none focus:ring-2 border-color  hover:file:bg-[#E95B21]"
                />
                {errors.sellerGstCertificate && (
                  <span className="text-red-500 text-xs">{errors.sellerGstCertificate}</span>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-secondary">Trade Name</label>
                <input
                  type="text"
                  placeholder="Enter Trade Name"
                  className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                  ref={inputRefs.tradeName}
                  value={tradeName}
                  onChange={(e) => setTradeName(e.target.value)}
                />
                {errors.tradeName && (
                  <span className="text-red-500 text-xs">{errors.tradeName}</span>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-secondary">Owner Name</label>
                <input
                  type="text"
                  placeholder="Enter Owner Name"
                  className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                  ref={inputRefs.name}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {errors.name && <span className="text-red-500 text-xs">{errors.name}</span>}
              </div>

              {/* Address Fields */}
              <div className="space-y-4 pt-2">
                <div>
                  <label className="text-sm font-medium text-secondary">Address line 1</label>
                  <input
                    type="text"
                    placeholder="Enter address line 1"
                    className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                    ref={inputRefs.addressLine1}
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                  />
                  {errors.addressLine1 && (
                    <span className="text-red-500 text-xs">{errors.addressLine1}</span>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary">Address line 2</label>
                  <input
                    type="text"
                    placeholder="Enter address line 2"
                    className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                    ref={inputRefs.addressLine2}
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                  />
                </div>
                <div className="flex gap-4">
                  <div className="w-1/2">
                    <label className="text-sm font-medium text-secondary">City</label>
                    <input
                      type="text"
                      placeholder="Enter city"
                      className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                      ref={inputRefs.city}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                    {errors.city && <span className="text-red-500 text-xs">{errors.city}</span>}
                  </div>
                  <div className="w-1/2">
                    <label className="text-sm font-medium text-secondary">State</label>
                    <input
                      type="text"
                      placeholder="Enter state"
                      className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                      ref={inputRefs.state}
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                    />
                    {errors.state && <span className="text-red-500 text-xs">{errors.state}</span>}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary">ZIP / Postcode</label>
                  <input
                    type="text"
                    placeholder="Enter ZIP / postcode"
                    className="mt-1 w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 border-color"
                    ref={inputRefs.zipCode}
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                  />
                  {errors.zipCode && <span className="text-red-500 text-xs">{errors.zipCode}</span>}
                </div>
              </div>

              {/* Brand Selection */}
              <div className="pt-2">
                <label className="block text-sm font-medium text-secondary mb-1">
                  Brands you deal in
                </label>
                <div
                  onClick={() => setIsOpen(!isOpen)}
                  className="w-full border rounded-xl px-4 py-2 cursor-pointer flex flex-wrap gap-1 items-center min-h-[42px] bg-white focus:outline-none focus:ring-2 border-color"
                >
                  {selectedBrands.length === 0 ? (
                    <span className="text-sm text-secondary">Select brands</span>
                  ) : (
                    selectedBrands.map((id) => (
                      <span
                        key={id}
                        className="text-sm secondary-background text-primary px-2 py-0.5 rounded-full"
                      >
                        {brandOptions.find((b) => b.id === id)?.name}
                      </span>
                    ))
                  )}
                </div>
                {isOpen && (
                  <div className="mt-2 border rounded-xl p-3 shadow-lg max-h-48 overflow-y-auto bg-white">
                    {brandOptions.map((brand) => (
                      <label
                        key={brand.id}
                        className="flex items-center gap-2 text-sm mb-2 cursor-pointer p-1 hover:bg-gray-100 rounded"
                      >
                        <input
                          type="checkbox"
                          value={brand.id}
                          checked={selectedBrands.includes(brand.id)}
                          onChange={handleBrandChange}
                          className="accent-[#E95B21]"
                        />
                        <span>{brand.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {selectedBrands.length > 0 && (
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="font-semibold text-primary">Brand Certificates of Authenticity</h3>
                  {selectedBrands.map((brandId) => {
                    const brand = brandOptions.find((b) => b.id === brandId);
                    if (!brand) return null;
                    return (
                      <div key={brandId}>
                        <label className="text-sm font-medium text-secondary">{brand.name}</label>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'sellerAuthCertificates', brandId)}
                          className="mt-1 w-full px-4 py-2 border rounded-lg text-sm bg-white file:mr-4 file:py-1 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:primary-background file:white-text hover:file:bg-[#E95B21]"
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* <Terms and Conditions Checkbox> */}
              <div className="flex items-center mt-4 ml-1">
                <input
                  type="checkbox"
                  id="termsSeller"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="text-[#E95B21] mr-2"
                />
                <label htmlFor="termsSeller" className="text-sm text-secondary">
                  I agree to the{' '}
                  <a
                    className="hover:underline hover:text-orange-600"
                    href={termsPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms and Conditions
                  </a>
                </label>
              </div>
              {errors.agreedToTerms && (
                <span className="text-red-500 text-xs">{errors.agreedToTerms}</span>
              )}
              <div className="flex justify-center pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting || uploading || !agreedToTerms}
                  className="w-1/2 md:w-1/3 primary-background hover:bg-[#6e1e61] white-text py-2 rounded-xl font-medium text-sm transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting || uploading ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </>,
        )}

      {(isSubmitting || uploading || isSendingOtp || isRegistering || isVerifyingOtp) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
          <Loader />
        </div>
      )}
    </>
  );
};

export default SignUp;
